<div class="geo-layout">
  <div class="map-col">
    <div class="map-toolbar omit-from-screenshot">
      @for (l of layerDefs; track l.key) {
        @if (l.key !== "cdps" || hasCdps()) {
          <label
            nz-checkbox
            [ngModel]="layers()[l.key]"
            (ngModelChange)="setLayer(l.key, $event)"
          >
            {{ l.name }}
          </label>
        }
      }
      @if (highlight(); as hl) {
        <label
          nz-checkbox
          nz-tooltip
          nzTooltipTitle="Mark the gather shown on the Display tab: its source, its receivers and the clicked trace"
          [ngModel]="showHighlight()"
          (ngModelChange)="showHighlight.set($event)"
        >
          <span class="hl-swatch"></span>{{ hl.label }}
        </label>
      }
      <span class="toolbar-label">Colour by</span>
      <app-segy-field-select
        class="colour-select"
        placeholder="none"
        [allowClear]="true"
        [options]="fieldOptions()"
        [value]="colorField()"
        (valueChange)="colorField.set($any($event) ?? null)"
      ></app-segy-field-select>
      <span class="muted">
        {{ counts().sources }} source · {{ counts().receivers }} receiver positions
      </span>
    </div>

    @if (options(); as opts) {
      <div
        echarts
        class="geo-chart"
        [options]="opts"
      ></div>
    }
  </div>

  <!-- Model grid box (preprep / brute_stack convention) -->
  <div class="box-col">
    <div class="box-title">
      Model grid box
      <span
        nz-icon
        nzType="info-circle"
        class="info-icon"
        nz-tooltip
        nzTooltipTitle="Node (i, j) sits at origin + R(angle)·(i·DX, j·DX)/XYSCALE, i < NX1, j < NX2. ANGLE is degrees counter-clockwise from +X (east), as in preprep's model_parameters."
      ></span>
    </div>

    <div class="box-grid">
      <label>X origin</label>
      <nz-input-number
        nzSize="small"
        [ngModel]="box().xOrigin"
        (ngModelChange)="setBox('xOrigin', $event)"
      ></nz-input-number>
      <label>Y origin</label>
      <nz-input-number
        nzSize="small"
        [ngModel]="box().yOrigin"
        (ngModelChange)="setBox('yOrigin', $event)"
      ></nz-input-number>
      <label>NX1</label>
      <nz-input-number
        nzSize="small"
        [nzMin]="1"
        [nzStep]="1"
        [nzPrecision]="0"
        [ngModel]="box().nx1"
        (ngModelChange)="setBox('nx1', $event)"
      ></nz-input-number>
      <label>NX2</label>
      <nz-input-number
        nzSize="small"
        [nzMin]="1"
        [nzStep]="1"
        [nzPrecision]="0"
        [ngModel]="box().nx2"
        (ngModelChange)="setBox('nx2', $event)"
      ></nz-input-number>
      <label>DX</label>
      <nz-input-number
        nzSize="small"
        [nzMin]="0"
        [ngModel]="box().dx"
        (ngModelChange)="setBox('dx', $event)"
      ></nz-input-number>
      <label>Angle (°)</label>
      <nz-input-number
        nzSize="small"
        [nzMin]="-360"
        [nzMax]="360"
        [nzStep]="0.5"
        [ngModel]="box().angle"
        (ngModelChange)="setBox('angle', $event ?? 0)"
      ></nz-input-number>
      <label nz-tooltip nzTooltipTitle="Model units per coordinate unit (e.g. 0.3048 when coordinates are in feet and DX in metres)">XYSCALE</label>
      <nz-input-number
        nzSize="small"
        [nzMin]="0"
        [nzStep]="0.1"
        [ngModel]="box().xyscale"
        (ngModelChange)="setBox('xyscale', $event || 1)"
      ></nz-input-number>
    </div>

    <div class="box-fit">
      <span class="fit-label">Fit angle</span>
      <nz-radio-group
        nzSize="small"
        [ngModel]="fitAngle()"
        (ngModelChange)="fitAngle.set($event)"
      >
        <label
          nz-radio-button
          [nzValue]="false"
          nz-tooltip
          nzTooltipTitle="Keep the angle typed above"
        >
          Use angle
        </label>
        <label
          nz-radio-button
          [nzValue]="true"
          nz-tooltip
          nzTooltipTitle="Rotate to the smallest rectangle round the data"
        >
          Fit angle
        </label>
      </nz-radio-group>

      <span class="fit-label">Padding</span>
      <span class="pad-controls">
        <nz-input-number
          nzSize="small"
          class="pad-input"
          [nzMin]="0"
          [nzStep]="100"
          [ngModel]="padding()"
          (ngModelChange)="padding.set($event ?? 0)"
        ></nz-input-number>
          <button
            nz-button
            nzSize="small"
            nz-tooltip
            nzTooltipTitle="Trim this much off every side"
            [disabled]="!resolvedBox()"
            (click)="padBox(-1)"
          >
            <span nz-icon nzType="minus"></span>
          </button>
          <button
            nz-button
            nzSize="small"
            nz-tooltip
            nzTooltipTitle="Add this much on every side"
            [disabled]="!resolvedBox()"
            (click)="padBox(1)"
          >
            <span nz-icon nzType="plus"></span>
          </button>
      </span>
    </div>

    <div class="box-actions">
      <button
        nz-button
        nzSize="small"
        nz-tooltip
        [nzTooltipTitle]="
          fitAngle()
            ? 'Find the tightest rotation, then set origin, NX1 and NX2 to enclose every shown point (plus padding)'
            : 'Set origin, NX1 and NX2 to enclose every shown point at this angle and DX (plus padding)'
        "
        (click)="fitBoxToData()"
      >
        <span nz-icon nzType="border-outer"></span> Fit to data
      </button>
      <button
        nz-button
        nzSize="small"
        nz-tooltip
        nzTooltipTitle="Copy as preprep model_parameters"
        [disabled]="!boxParams()"
        (click)="copyParams()"
      >
        <span nz-icon nzType="copy"></span>
      </button>
      <button nz-button nzType="link" nzSize="small" (click)="clearBox()">clear</button>
    </div>

    @if (boxMessage(); as msg) {
      <div class="muted box-message">{{ msg }}</div>
    }

    @if (resolvedBox(); as b) {
      <div class="box-readout">
        <div class="ro-row">
          <span>NX1 length</span><span>{{ fmtNum(b.len1) }}</span>
        </div>
        <div class="ro-row">
          <span>NX2 length</span><span>{{ fmtNum(b.len2) }}</span>
        </div>
        @for (c of b.corners; track $index) {
          <div class="ro-row">
            <span>Corner {{ ["(0, 0)", "(NX1, 0)", "(NX1, NX2)", "(0, NX2)"][$index] }}</span>
            <span>{{ fmtNum(c[0]) }}, {{ fmtNum(c[1]) }}</span>
          </div>
        }
        @for (cov of boxCoverage(); track cov.name) {
          <div class="ro-row">
            <span>{{ cov.name }} inside</span>
            <span [class.warn]="cov.inside < cov.total">
              {{ cov.inside }} / {{ cov.total }}
            </span>
          </div>
        }
      </div>
    } @else {
      <div class="muted box-message">
        Enter an origin, NX1, NX2 and DX (or set DX and fit to the data)
        to draw the box.
      </div>
    }
  </div>
</div>
